import React, { useState } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Collapse,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Sparkles } from 'lucide-react';
import dayjs from 'dayjs';
import { Task } from '../../../types';
import { TaskFormWrapper } from './TaskFormWrapper';

interface MedicalRecordReviewFormProps {
  task: Task;
  onSubmit: (formData: any) => void;
}

interface AIAnalysis {
  provider: string;
  recordDate: string;
  diagnosis: string;
  treatment: string;
  relevantFindings: string;
  followUpRecommended: boolean;
  followUpNotes: string;
}

const mockAIAnalysis = (file: File): Promise<AIAnalysis> => {
  // Simulate AI processing delay
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        provider: 'City General Hospital',
        recordDate: new Date().toISOString(),
        diagnosis: 'Patient presents with lower back pain following workplace incident. MRI shows L4-L5 disc herniation.',
        treatment: 'Prescribed physical therapy 3x/week for 6 weeks. Pain management with NSAIDs.',
        relevantFindings: '- Decreased range of motion in lumbar spine\n- Positive straight leg raise test\n- No signs of nerve root compression\n- Previous history of minor back strain',
        followUpRecommended: true,
        followUpNotes: 'Schedule follow-up in 6 weeks to assess progress. Consider referral to spine specialist if symptoms persist.',
      });
    }, 2000);
  });
};

export const MedicalRecordReviewForm: React.FC<MedicalRecordReviewFormProps> = ({
  task,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    recordDate: dayjs(),
    provider: '',
    recordType: '',
    diagnosis: '',
    treatment: '',
    relevantFindings: '',
    followUpNeeded: false,
    followUpNotes: '',
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<AIAnalysis | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        await handleAIAnalysis(file);
      }
    },
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: false,
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAIAnalysis = async (file: File) => {
    setIsAnalyzing(true);
    setAnalysisError(null);
    try {
      const analysis = await mockAIAnalysis(file);
      setAiSuggestion(analysis);
    } catch (error) {
      setAnalysisError('Failed to analyze the document. Please try again or proceed with manual entry.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const applyAISuggestions = () => {
    if (!aiSuggestion) return;

    setFormData(prev => ({
      ...prev,
      recordDate: dayjs(aiSuggestion.recordDate),
      provider: aiSuggestion.provider,
      diagnosis: aiSuggestion.diagnosis,
      treatment: aiSuggestion.treatment,
      relevantFindings: aiSuggestion.relevantFindings,
      followUpNeeded: aiSuggestion.followUpRecommended,
      followUpNotes: aiSuggestion.followUpNotes,
    }));
  };

  return (
    <TaskFormWrapper task={task} onSubmit={() => onSubmit(formData)}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Sparkles size={20} className="text-primary" />
          AI-Assisted Record Review
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload a medical record document to get AI-powered suggestions for form completion.
        </Typography>

        {!selectedFile && (
          <Box
            {...getRootProps()}
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'center',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <Upload 
              size={32}
              className={isDragActive ? 'text-primary' : 'text-gray-400'}
            />
            <Typography sx={{ mt: 2 }}>
              {isDragActive ? (
                'Drop the medical record here'
              ) : (
                'Drag and drop a medical record, or click to select'
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Supported formats: PDF, JPG, PNG
            </Typography>
          </Box>
        )}

        {selectedFile && (
          <Box sx={{ mb: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <FileText size={24} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                onClick={() => {
                  setSelectedFile(null);
                  setAiSuggestion(null);
                }}
              >
                Remove
              </Button>
            </Paper>
          </Box>
        )}

        {isAnalyzing && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={32} />
            <Typography sx={{ mt: 1 }} color="text.secondary">
              Analyzing document...
            </Typography>
          </Box>
        )}

        {analysisError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {analysisError}
          </Alert>
        )}

        <Collapse in={Boolean(aiSuggestion)}>
          <Box sx={{ mt: 2 }}>
            <Alert 
              severity="success"
              action={
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyAISuggestions}
                >
                  Apply Suggestions
                </Button>
              }
            >
              AI analysis complete! Review the suggestions and apply them to the form.
            </Alert>
          </Box>
        </Collapse>
      </Paper>

      <DatePicker
        label="Record Date"
        value={formData.recordDate}
        onChange={(date) => handleChange('recordDate', date)}
        slotProps={{
          textField: { fullWidth: true }
        }}
      />

      <TextField
        label="Healthcare Provider"
        fullWidth
        value={formData.provider}
        onChange={(e) => handleChange('provider', e.target.value)}
      />

      <FormControl fullWidth>
        <InputLabel>Record Type</InputLabel>
        <Select
          value={formData.recordType}
          label="Record Type"
          onChange={(e) => handleChange('recordType', e.target.value)}
        >
          <MenuItem value="admission">Admission Records</MenuItem>
          <MenuItem value="discharge">Discharge Summary</MenuItem>
          <MenuItem value="progress">Progress Notes</MenuItem>
          <MenuItem value="lab">Laboratory Results</MenuItem>
          <MenuItem value="imaging">Imaging Reports</MenuItem>
          <MenuItem value="consultation">Consultation Notes</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Diagnosis"
        fullWidth
        multiline
        rows={2}
        value={formData.diagnosis}
        onChange={(e) => handleChange('diagnosis', e.target.value)}
      />

      <TextField
        label="Treatment Details"
        fullWidth
        multiline
        rows={2}
        value={formData.treatment}
        onChange={(e) => handleChange('treatment', e.target.value)}
      />

      <TextField
        label="Relevant Findings"
        fullWidth
        multiline
        rows={3}
        value={formData.relevantFindings}
        onChange={(e) => handleChange('relevantFindings', e.target.value)}
      />

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.followUpNeeded}
              onChange={(e) => handleChange('followUpNeeded', e.target.checked)}
            />
          }
          label="Follow-up Required"
        />
      </Box>

      {formData.followUpNeeded && (
        <TextField
          label="Follow-up Notes"
          fullWidth
          multiline
          rows={2}
          value={formData.followUpNotes}
          onChange={(e) => handleChange('followUpNotes', e.target.value)}
        />
      )}
    </TaskFormWrapper>
  );
};